import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { PayPalNamespace } from '../../../hooks/usePayPalSdk';

export interface PayPalButtonsProps {
    paypal: PayPalNamespace;
    /** Von `POST /marketplace/checkout` erzeugte PayPal-Order (Betrag + Plattformgebühr serverseitig) */
    paypalOrderId: string;
    onApprove: (paypalOrderId: string) => Promise<void> | void;
    onCancel?: () => void;
    onError?: (err: unknown) => void;
}

/**
 * Rendert die PayPal-Buttons des JS-SDK. `createOrder` liefert nur die bereits
 * serverseitig angelegte PayPal-Order zurück — Preis und Empfänger kann der
 * Browser nicht beeinflussen. Kein Popup-Polling: die Genehmigung kommt über
 * `onApprove`.
 */
const PayPalButtons: React.FC<PayPalButtonsProps> = ({ paypal, paypalOrderId, onApprove, onCancel, onError }) => {
    const container = useRef<HTMLDivElement | null>(null);
    // Handler über Refs, damit ein Re-Render die Buttons nicht neu aufbaut
    const handlers = useRef({ onApprove, onCancel, onError });
    handlers.current = { onApprove, onCancel, onError };

    useEffect(() => {
        const element = container.current;
        if (!element) return undefined;

        const buttons = paypal.Buttons({
            style: { layout: 'vertical', shape: 'rect', label: 'pay' },
            createOrder: async () => paypalOrderId,
            onApprove: async (data) => {
                await handlers.current.onApprove(data.orderID);
            },
            onCancel: () => handlers.current.onCancel?.(),
            onError: (err) => handlers.current.onError?.(err),
        });
        buttons.render(element).catch((err: unknown) => handlers.current.onError?.(err));

        return () => {
            buttons.close?.().catch(() => undefined);
        };
    }, [paypal, paypalOrderId]);

    return <Box ref={container} data-testid="paypal-buttons" sx={{ minHeight: 48, width: '100%' }} />;
};

export default PayPalButtons;
